/**
 * MyWorkView - Self-contained wrapper for MyWork component
 *
 * This wrapper fetches its own data and provides callbacks,
 * making MyWork work in the new navigation system without
 * requiring props from WireframeApp.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import MyWork from '../MyWork';
import { usePinnedQuotes } from '../MyPinnedQuotes';

const apiScopes = [
  'api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user',
];

export default function MyWorkView({ userEmail, isPM, isAdmin, onNavigateLegacy }) {
  const { instance, accounts } = useMsal();
  const { pinnedQuoteNumbers } = usePinnedQuotes();
  const [activeSessions, setActiveSessions] = useState({});
  const [allQuotes, setAllQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Acquire access token
  const acquireAccessToken = useCallback(async () => {
    if (accounts.length === 0) throw new Error('No logged in user.');
    const account = accounts[0];
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('Session expired, redirecting to login...');
        await instance.loginRedirect({ scopes: apiScopes, account });
        return null;
      }
      throw error;
    }
  }, [instance, accounts]);

  // Load active sessions
  const loadActiveSessions = useCallback(async () => {
    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch('/api/me/time/active', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setActiveSessions(data.sessions || {});
    } catch (err) {
      console.error('Error loading active sessions:', err);
      setActiveSessions({});
    }
  }, [acquireAccessToken]);

  // Load quotes for enrichment
  const loadQuotes = useCallback(async () => {
    try {
      const token = await acquireAccessToken();
      if (!token) return;

      const res = await fetch('/api/quotes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAllQuotes(data.quotes || []);
    } catch (err) {
      console.error('Error loading quotes:', err);
      setAllQuotes([]);
    }
  }, [acquireAccessToken]);

  // Initial load
  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([loadActiveSessions(), loadQuotes()]);
      setLoading(false);
    }
    init();
  }, [loadActiveSessions, loadQuotes]);

  // Poll for updates
  useEffect(() => {
    const interval = setInterval(loadActiveSessions, 5000);
    return () => clearInterval(interval);
  }, [loadActiveSessions]);

  // Listen for time-updated events
  useEffect(() => {
    const handleTimeUpdated = () => loadActiveSessions();
    window.addEventListener('time-updated', handleTimeUpdated);
    return () => window.removeEventListener('time-updated', handleTimeUpdated);
  }, [loadActiveSessions]);

  // Handle quote selection
  const handleSelectQuote = useCallback((quote) => {
    // In new navigation, we could navigate to a project detail view
    // For now, just log
    console.log('[MyWorkView] Select quote:', quote);
  }, []);

  // Handle job navigation
  const handleNavigateToJob = useCallback((quoteNumber, orderNumber, jobId) => {
    console.log('[MyWorkView] Navigate to job:', { quoteNumber, orderNumber, jobId });
  }, []);

  // Pause job
  const handlePauseJob = useCallback(async (jobId) => {
    try {
      const token = await acquireAccessToken();
      if (!token) return;

      await fetch(`/api/time/${jobId}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error pausing job:', err);
    }
  }, [acquireAccessToken]);

  // Resume job
  const handleResumeJob = useCallback(async (jobId) => {
    try {
      const token = await acquireAccessToken();
      if (!token) return;

      await fetch(`/api/time/${jobId}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.dispatchEvent(new CustomEvent('time-updated'));
    } catch (err) {
      console.error('Error resuming job:', err);
    }
  }, [acquireAccessToken]);

  // Complete job (simplified - opens in legacy for now)
  const handleCompleteJob = useCallback(async (jobId) => {
    // For now, just pause - full complete flow needs the modal
    console.log('[MyWorkView] Complete job - needs modal:', jobId);
    alert('Complete flow coming soon. Use pause for now.');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Check if we have any content to display
  const hasActiveSessions = Object.keys(activeSessions).length > 0;
  const hasPinnedQuotes = pinnedQuoteNumbers && pinnedQuoteNumbers.length > 0;
  const hasContent = hasActiveSessions || hasPinnedQuotes;

  return (
    <div className="p-4">
      <MyWork
        allQuotes={allQuotes}
        activeSessions={activeSessions}
        onSelectQuote={handleSelectQuote}
        onNavigateToJob={handleNavigateToJob}
        onPauseJob={handlePauseJob}
        onResumeJob={handleResumeJob}
        onCompleteJob={handleCompleteJob}
        pendingTicketsReady={0}
        onAssignPendingTickets={() => {}}
      />
      {/* Empty state fallback - shown when MyWork returns null (no sessions and no pinned quotes) */}
      {!hasContent && (
        <div className="text-center py-12 bg-slate-200 rounded-lg border border-gray-200">
          <div className="text-gray-500 text-4xl mb-3">⏱️</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Active Work</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            You don't have any active time sessions or pinned projects. Navigate to a project in the
            <strong className="text-purple-600"> Production</strong> section to start tracking time.
          </p>
          <button
            onClick={() => window.location.hash = '#/production/pa-queue'}
            className="mt-4 px-4 py-2 bg-purple-600 text-gray-900 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Production
          </button>
        </div>
      )}
    </div>
  );
}
